import type { EntityWithAuditFields, OmitAuditFields } from '@pkg/db/schema'
import { getTableById } from '@pkg/db/utils'
import { appError } from '@pkg/shared/errors'
import type { HLC, HLCString } from './hlc'
import type { MutateFn, MutateFnOptions, RemoveFn } from './mutate'
import { computeDiff } from './mutateInternals'
import type { SyncDatabase } from './schema'

export type TxStatus = 'open' | 'committing' | 'committed' | 'rolled-back'

export interface TransactionSnapshot {
  readonly status: TxStatus | 'idle'
  readonly size: number
  readonly hasChanges: boolean
}

export interface StagedEntry<T = Record<string, unknown>> {
  table: string
  id: string
  /** Snapshot of the projection row at first stage (or null if it does not exist yet). */
  baseExisting: T | null
  /** Accumulated overlay across all stage calls on this row. */
  pending: Partial<T>
  /** Per-column intentTs recorded at stage time (used as the message's intentTs at flush). */
  intentTsByColumn: Record<string, HLCString>
}

export interface DeferredTransactionDeps<T extends OmitAuditFields<EntityWithAuditFields>> {
  /** Returns the same lazily-initialized HLC the immediate `mutate` uses — never mints a fallback nodeId. */
  ensureHlc: () => Promise<HLC>
  /** Read the current projection row for `(table, id)`. Called once per row at first stage. */
  readExisting: (table: string, id: string) => Promise<T | null>
  /** Validate the merged `{ ...existing, ...overlay, ...incoming }` and return the validated value. Throw on failure. */
  validate: (table: string, merged: T) => T
  /**
   * Persist all staged entries atomically. Receives a snapshot of the buffer and the shared transactionTs.
   * MUST be atomic — partial success is not allowed. Implementations must wrap all writes in a single
   * `db.transaction()` (or equivalent) so that any thrown error rolls back every message and projection
   * update. The deferred transaction's retry contract (status reverts to 'open' on failure, buffer
   * preserved) depends on this invariant.
   */
  flush: (entries: ReadonlyArray<StagedEntry<T>>, transactionTs: HLCString) => Promise<void>
}

export interface DeferredTransaction<DB extends SyncDatabase> {
  mutate: MutateFn<DB>
  remove: RemoveFn<DB>
  restore: RemoveFn<DB>
  commit: () => Promise<void>
  rollback: () => void
  /**
   * Register a handler that runs before `commit` flushes the buffer. Handlers run
   * sequentially in registration order while status is still `open`, so they may
   * call `mutate` / `remove` / `restore` to stage additional rows that flush in
   * the same atomic write. If a handler throws, commit aborts and status reverts
   * to `open` with the buffer preserved (same as a flush failure).
   * Returns an unsubscribe function.
   */
  onBeforeCommit(handler: () => void | Promise<void>): () => void
  readonly status: TxStatus
  readonly staged: ReadonlyArray<StagedEntry>
  subscribe(listener: () => void): () => void
  getSnapshot(): TransactionSnapshot
}

const DEFAULT_IGNORED = ['id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'searchVector']

export function createDeferredTransaction<DB extends SyncDatabase>(
  deps: DeferredTransactionDeps<OmitAuditFields<EntityWithAuditFields>>,
): DeferredTransaction<DB> {
  const buffer = new Map<string, StagedEntry>()
  // Guards against parallel `tx.mutate(sameId, ...)` racing on `readExisting`.
  const inFlight = new Map<string, Promise<StagedEntry>>()
  let status: TxStatus = 'open'

  const beforeCommitHandlers = new Set<() => void | Promise<void>>()
  const listeners = new Set<() => void>()
  let cachedSnapshot: TransactionSnapshot = Object.freeze({
    status,
    size: buffer.size,
    hasChanges: buffer.size > 0,
  })
  let cachedStaged: ReadonlyArray<StagedEntry> = Object.freeze([])

  const refreshSnapshot = (): void => {
    cachedSnapshot = Object.freeze({
      status,
      size: buffer.size,
      hasChanges: buffer.size > 0,
    })
    cachedStaged = Object.freeze([...buffer.values()])
  }

  const notify = (): void => {
    refreshSnapshot()
    for (const listener of listeners) listener()
  }

  const stage = async <T extends EntityWithAuditFields>(data: T, _opts?: MutateFnOptions<DB>): Promise<T> => {
    if (status !== 'open') throw appError('INTERNAL', `Cannot mutate on a ${status} transaction (closed)`)
    if (!data?.id) throw appError('INTERNAL', 'Data must have an id to be staged')

    const table = getTableById(data.id)
    let entry = buffer.get(data.id)
    if (!entry) {
      let pending = inFlight.get(data.id)
      if (!pending) {
        pending = (async () => {
          const baseExisting = (await deps.readExisting(table, data.id)) ?? null
          const fresh: StagedEntry = { table, id: data.id, baseExisting, pending: {}, intentTsByColumn: {} }
          return fresh
        })()
        inFlight.set(data.id, pending)
      }
      entry = await pending
    }

    const mergedBefore = { ...(entry.baseExisting ?? {}), ...entry.pending } as T
    const mergedAfter = { ...mergedBefore, ...data } as T

    let validated: T
    try {
      validated = deps.validate(table, mergedAfter as unknown as OmitAuditFields<EntityWithAuditFields>) as T
    } catch (err) {
      // Validation failed: do not commit the entry to the buffer. Drop the in-flight
      // read-existing cache so a retry will re-read.
      inFlight.delete(data.id)
      throw err
    }

    // Validation succeeded: commit the entry to the buffer (idempotent if already present).
    if (!buffer.has(data.id)) {
      buffer.set(data.id, entry)
    }
    inFlight.delete(data.id)

    const hlc = await deps.ensureHlc()
    const stagedAt = hlc.now()
    const cols = computeDiff(mergedBefore as object, validated as object, DEFAULT_IGNORED)
    for (const col of cols) {
      ;(entry.pending as Record<string, unknown>)[col as string] = (validated as Record<string, unknown>)[col as string]
      entry.intentTsByColumn[col as string] = stagedAt
    }

    notify()
    return validated
  }

  const stageDeleteIntent = async (id: string, deleteKey: string): Promise<void> => {
    if (status !== 'open') throw appError('INTERNAL', `Cannot mutate on a ${status} transaction (closed)`)

    const table = getTableById(id)
    let entry = buffer.get(id)
    if (!entry) {
      let pending = inFlight.get(id)
      if (!pending) {
        pending = (async () => {
          const baseExisting = (await deps.readExisting(table, id)) ?? null
          const fresh: StagedEntry = { table, id, baseExisting, pending: {}, intentTsByColumn: {} }
          return fresh
        })()
        inFlight.set(id, pending)
      }
      entry = await pending
      if (!buffer.has(id)) buffer.set(id, entry)
      inFlight.delete(id)
    }

    ;(entry.intentTsByColumn as Record<string, string>).deletedAt = deleteKey
    notify()
  }

  const remove: RemoveFn<DB> = async id => {
    const hlc = await deps.ensureHlc()
    await stageDeleteIntent(id, hlc.now())
  }

  const restore: RemoveFn<DB> = async id => {
    await stageDeleteIntent(id, 'restore')
  }

  const onBeforeCommit = (handler: () => void | Promise<void>): (() => void) => {
    beforeCommitHandlers.add(handler)
    return () => {
      beforeCommitHandlers.delete(handler)
    }
  }

  const commit = async () => {
    if (status === 'committed') return
    if (status !== 'open') throw appError('INTERNAL', `Cannot commit a ${status} transaction`)

    // Drain registered before-commit handlers while status is still 'open' so they
    // can call mutate/remove/restore to stage additional rows in this same commit.
    for (const handler of beforeCommitHandlers) {
      await handler()
    }

    const entries = [...buffer.values()]
    if (entries.length === 0) {
      status = 'committed'
      notify()
      return
    }

    status = 'committing'
    notify()
    const hlc = await deps.ensureHlc()
    const transactionTs = hlc.now()
    try {
      await deps.flush(entries, transactionTs)
      status = 'committed'
      notify()
    } catch (err) {
      // Allow retry — keep buffer intact and revert status to open.
      status = 'open'
      notify()
      throw err
    }
  }

  const rollback = () => {
    if (status === 'committed') return
    status = 'rolled-back'
    buffer.clear()
    notify()
  }

  return {
    // Structural bridge: `stage` is single-T generic, `MutateFn<DB>` is heterogeneous-table.
    mutate: stage as unknown as MutateFn<DB>,
    remove,
    restore,
    commit,
    rollback,
    onBeforeCommit,
    get status() {
      return status
    },
    get staged() {
      return cachedStaged
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    getSnapshot: (): TransactionSnapshot => cachedSnapshot,
  }
}
